/***
 *
 *   API Key Editor
 *   Create or edit and API key.
 *
 **********/

import { useState, useEffect, useCallback } from "react";
import Axios from "axios";
import {
  Animate,
  Card,
  Form,
  Alert,
  Breadcrumb,
  Row,
  Label,
  Input,
  Loader,
  useNavigate,
  useSearchParams,
  useAPI,
} from "components/lib";

// Scope labels and availability
const SCOPE_CONFIG = {
  maps: { label: "Maps API", available: true },
  autocomplete: { label: "Autocomplete API", available: true },
  geocode: { label: "Geocoding API", available: true },
  directions: { label: "Directions API", available: true },
  map_matching: { label: "Map Matching API", available: true },
  optimize: { label: "Optimization API", available: true },
  matrix: { label: "Matrix API", available: true },
  isochrone: { label: "Isochrone API", available: true },
  elevation: { label: "Elevation API", available: true },
  navigation: { label: "Navigation SDK", available: false },
  tiles: { label: "Tiles API", available: false },
  static_images: { label: "Static Images API", available: false },
};

const ALL_SCOPES = Object.keys(SCOPE_CONFIG);

export function APIKeyEditor({ t }) {
        const userPermission_local=JSON.parse(localStorage.getItem('user'))?.permission;

  const navigate = useNavigate();

  // state
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(false);
  const [newAPIKey, setNewAPIKey] = useState(false);
  const [searchParams] = useSearchParams();

  // fetch the scopes
  const scopes = useAPI("/api/key/scopes");

  const fetch = useCallback(
    async (id) => {
      setLoading(true);

      const res = await Axios.get(`/api/key/${id}`);

      res.data.data.length ? setData(res.data.data[0]) : navigate("/404");

      setLoading(false);
    },
    [location.search, navigate]
  );

  useEffect(() => {
    // editing existing key?
    const id = searchParams.get("id");
    id && fetch(id);
  }, [fetch, searchParams]);

  if (scopes.loading) return <Loader />;

  // Build scope options with proper labels; disable unavailable APIs (navigation, tiles, static_images)
  const scopeOptions = ALL_SCOPES.map(scope => ({
    value: scope,
    label: SCOPE_CONFIG[scope].label,
    disabled: !SCOPE_CONFIG[scope].available,
    tooltip: !SCOPE_CONFIG[scope].available ? "Coming soon" : null,
  }));
  
  return (
    <Animate>
      <Row>
        <Breadcrumb
          items={[
            {
              name: t("api_keys.edit.breadcrumbs.keys"),
              url: "/keys",
            },
            {
              name: `${
                data
                  ? t("api_keys.edit.breadcrumbs.edit")
                  : t("api_keys.edit.breadcrumbs.create")
              }`,
              url: "/Keys/create",
            },
          ]}
        />
      </Row>

      {newAPIKey ? (
        <Card title={t("api_keys.edit.message.title")}>
          <Row>
            <Alert
              variant="warning"
              title={t("api_keys.edit.message.description")}
              button={{
                text: t("api_keys.edit.message.button"),
                url: "/keys",
              }}
            />
          </Row>

          <div>
            <Label>Your Key:</Label>
            <Input value={newAPIKey} />
          </div>
        </Card>
      ) : (
        <Card
          title={`${
            data
              ? t("api_keys.edit.breadcrumbs.edit")
              : t("api_keys.edit.breadcrumbs.create")
          } Key`}
          loading={loading}
        >
          <Form
            inputs={{
              name: {
                label: t("api_keys.edit.form.name.label"),
                type: "text",
                required: true,
                value: data?.name || "",
                placeholder: "",
                errorMessage: t("api_keys.edit.form.name.error"),
              },
              allowed_websites: {
                label: t("api_keys.edit.form.allowed_websites.label"),
                type: "text",
                placeholder: "example.com, another-site.com",
                description: t("api_keys.edit.form.allowed_websites.description"),
                errorMessage: t("api_keys.edit.form.allowed_websites.error"),
              },
              scope: {
                label: t("api_keys.edit.form.scope.label"),
                type: "checkboxgroup",
                required: true,
                defaultValue: ["maps"],
                options: scopeOptions,
                errorMessage: t("api_keys.edit.form.scope.error"),
              },
            }}
            url={data ? `/api/key/${data.id}` : "/api/key"}
            method={data ? "PATCH" : "POST"}
            buttonText={t("api_keys.edit.form.button")}
            callback={(res) => {
              data
                ? navigate("/keys")
                : setNewAPIKey(res?.data?.data?.full_key);
              }}
              className={userPermission_local!=='user'?'':'hidden'}
          />
        </Card>
      )}
    </Animate>
  );
}
